<script setup lang="ts">
import { computed } from 'vue';
import BetaTag from '../BetaTag/BetaTag.vue';
import ButtonV2 from '../ButtonV2/ButtonV2.vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import type { FeatureBannerProps } from './feature-banner';

const FEATURE_BANNER_ICON_SIZE = 28;

const props = withDefaults(defineProps<FeatureBannerProps>(), {
  showBetaTag: false,
  disabled: false,
  actionLayout: 'inline',
  actionVariant: 'primary',
});

const isStackedAction = computed(() => props.actionLayout === 'stacked');

const emits = defineEmits<{
  action: [];
}>();

const handleAction = (): void => {
  emits('action');
};
</script>

<template>
  <aside
    class="to-primary-50/40 relative overflow-hidden rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white shadow-sm"
    :aria-label="props.ariaLabel"
  >
    <div
      class="bg-primary-100/40 pointer-events-none absolute -top-10 left-8 h-28 w-28 rounded-full blur-2xl"
      aria-hidden="true"
    />
    <div
      class="pointer-events-none absolute -bottom-8 left-8 h-20 w-20 rounded-full bg-slate-100/80 blur-xl"
      aria-hidden="true"
    />

    <div
      class="relative flex flex-col gap-4 px-4 py-4 md:px-4"
      :class="
        isStackedAction
          ? undefined
          : 'md:flex-row md:items-center md:justify-between'
      "
    >
      <div class="flex min-w-0 flex-1 items-start gap-4">
        <div
          class="border-primary-100 text-primary-600 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border bg-white shadow-sm"
        >
          <HugeIcon :icon="props.icon" :size="FEATURE_BANNER_ICON_SIZE" />
        </div>

        <div class="min-w-0 flex-1 space-y-2">
          <div class="flex items-center gap-2">
            <BetaTag v-if="props.showBetaTag" />
            <p class="text-sm font-semibold text-slate-900">
              {{ props.title }}
            </p>
          </div>
          <p
            v-if="props.description || $slots.description"
            class="text-sm leading-relaxed text-slate-600"
          >
            <slot name="description">
              {{ props.description }}
            </slot>
          </p>
        </div>
      </div>

      <div
        v-if="props.actionLabel"
        class="relative z-10 flex w-full shrink-0"
        :class="isStackedAction ? 'justify-center' : 'md:w-auto'"
      >
        <ButtonV2
          :label="props.actionLabel"
          :icon="props.actionIcon"
          :variant="props.actionVariant"
          size="sm"
          :class="isStackedAction ? 'w-auto' : 'w-full md:w-auto'"
          :disabled="props.disabled"
          @click="handleAction"
        />
      </div>
    </div>
  </aside>
</template>
